/* @flow */
import { createSelector } from 'reselect';
import moment from 'moment';

import locations from '../locations';
import fareEstimates from '../fare-estimates';
import type { TripRequest } from '../../data/TripRequests';
import PRODUCTS from '../../data/Products';

export const getNewTripRequest = ({ tripRequests: { newTripRequest } }: Object) => ({
  ...newTripRequest,
  fromTime: newTripRequest.fromTime ? moment.parseZone(newTripRequest.fromTime) : null,
  toTime: newTripRequest.toTime ? moment.parseZone(newTripRequest.toTime) : null,
  day: newTripRequest.day ? moment.parseZone(newTripRequest.day) : null,
});

export const getTripRequestById = (state: Object, id: string): ?TripRequest =>
  ((state.entities || {}).tripRequests || {})[id];

export const getAllTripRequests = createSelector(
  state => (state.entities || {}).tripRequests || {},
  tripRequests => Object.values(tripRequests).reverse()
);

export const getNewTripRequestOrigin = createSelector(
  getNewTripRequest,
  state => state,
  ({ originId }, state) => locations.selectors.getLocationById(state, originId)
);

export const getNewTripRequestDestination = createSelector(
  getNewTripRequest,
  state => state,
  ({ destinationId }, state) => locations.selectors.getLocationById(state, destinationId)
);

export const getNewTripRequestProduct = createSelector(
  getNewTripRequest,
  ({ product }) => (product ? PRODUCTS[product] : null)
);

export const getNewTripRequestFareEstimate = createSelector(
  getNewTripRequest,
  state => state,
  (newTripRequest, state) =>
    newTripRequest.fareEstimateId
      ? fareEstimates.selectors.getFareEstimateById(state, newTripRequest.fareEstimateId)
      : null
);

export const getNewTripRequestFareEstimates = createSelector(
  getNewTripRequest,
  state => state,
  ({ originId, destinationId, useCase }, state) => {
    if (!originId || !destinationId) return null;
    return fareEstimates.selectors.getFareEstimatesByLocationIds(
      state,
      originId,
      destinationId,
      useCase
    );
  }
);

export const makeTripRequestGetter = () =>
  createSelector(
    getTripRequestById,
    (tripRequest: TripRequest) => tripRequest
  );

export const makeTripRequestUseCaseGetter = () =>
  createSelector(
    getTripRequestById,
    tripRequest => (tripRequest ? tripRequest.trip.useCase : null)
  );

export const makeTripRequestProductIdGetter = () =>
  createSelector(
    getTripRequestById,
    tripRequest => (tripRequest ? tripRequest.product : null)
  );

export const makeTripRequestProductGetter = () =>
  createSelector(
    makeTripRequestProductIdGetter(),
    productId => (productId ? PRODUCTS[productId] : null)
  );

export const makeTripRequestOriginGetter = () =>
  createSelector(
    getTripRequestById,
    tripRequest => (tripRequest ? tripRequest.trip.origin : null)
  );

export const makeTripRequestDestinationGetter = () =>
  createSelector(
    getTripRequestById,
    tripRequest => (tripRequest ? tripRequest.trip.destination : null)
  );

export const makeTripRequestDayGetter = () =>
  createSelector(
    getTripRequestById,
    tripRequest =>
      tripRequest ? moment.parseZone(tripRequest.delivery_from || tripRequest.created_at) : null
  );

export const makeTripRequestFromTimeGetter = () =>
  createSelector(
    getTripRequestById,
    tripRequest => (tripRequest ? moment.parseZone(tripRequest.delivery_from) : null)
  );

export const makeTripRequestToTimeGetter = () =>
  createSelector(
    getTripRequestById,
    tripRequest => (tripRequest ? moment.parseZone(tripRequest.delivery_to) : null)
  );

export const makeTripRequestCompletedTimeGetter = () =>
  createSelector(
    getTripRequestById,
    tripRequest => (tripRequest ? moment.parseZone(tripRequest.completed_at) : null)
  );

export const makeTripRequestFareGetter = () =>
  createSelector(
    getTripRequestById,
    tripRequest => (tripRequest ? tripRequest.fare : null)
  );

export const makeTripRequestDescriptionGetter = () =>
  createSelector(
    getTripRequestById,
    tripRequest => (tripRequest ? tripRequest.trip.description : null)
  );

export const makeTripRequestFareEstimateGetter = () =>
  createSelector(
    getTripRequestById,
    tripRequest => (tripRequest ? tripRequest.fare_estimate : null)
  );

export const makeTripRequestLabelGetter = () =>
  createSelector(
    getTripRequestById,
    tripRequest => (tripRequest ? tripRequest.label : null)
  );

export const makeTripRequestEtaGetter = () =>
  createSelector(
    getTripRequestById,
    tripRequest => (tripRequest && tripRequest.eta ? moment.parseZone(tripRequest.eta) : null)
  );

export const makeTripRequestStatusGetter = () =>
  createSelector(
    getTripRequestById,
    tripRequest => (tripRequest ? tripRequest.trip_status : null)
  );



// WEBPACK FOOTER //
// ./src/redux/trip-requests/selectors.js